"use client";

import Link from "next/link";
import Image, { type StaticImageData } from "next/image";
import { type VariantProps, cva } from "class-variance-authority";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { getCtaDataAttrs } from "@/lib/analytics/cta-attrs";

const containerVariant = cva(
	"group/mobile_app_button grid rounded-full shadow-custom transition-colors duration-300",
	{
		variants: {
			size: {
				xs: "h-10 grid-cols-[auto_auto_1fr] gap-2 px-3",
				sm: "h-12 grid-cols-[auto_auto_1fr] gap-3 px-4",
				md: "h-14 grid-cols-[auto_auto_1fr] gap-4 px-5",
				lg: "h-16 grid-cols-[auto_auto_1fr] gap-5 px-6",
			},
		},
		defaultVariants: {
			size: "lg",
		},
	},
);

const iconVariant = cva("shrink-0 transition-all", {
	variants: {
		size: {
			xs: "size-4",
			sm: "size-5",
			md: "size-6",
			lg: "size-7",
		},
	},
	defaultVariants: {
		size: "lg",
	},
});

const separatorVariant = cva("", {
	variants: {
		size: { xs: "h-3", sm: "h-4", md: "h-5", lg: "h-6" },
	},
	defaultVariants: {
		size: "lg",
	},
});

const spanVariants = cva("group-hover/mobile_app_button:text-dark leading-tight text-white transition-colors", {
	variants: {
		size: {
			xs: "text-[0.45rem]",
			sm: "text-[0.55rem]",
			md: "text-[0.65rem]",
			lg: "text-xs",
		},
	},
	defaultVariants: {
		size: "lg",
	},
});

type Props = VariantProps<typeof containerVariant> & {
	store: string;
	href: string;
	labelTop?: string;
	className?: string;
	background?: "light" | "dark";
	iconLight: StaticImageData;
	iconDark?: StaticImageData;
	/** GA4 `cta_location` — use page-specific values when the same stores appear in multiple places. */
	ctaLocation?: string;
};

const BACKGROUND_CLASS = "bg-dark-1 dark:bg-core-midnight hover:bg-core-secondary text-white hover:text-dark";

export default function MobileAppButton({
	store,
	href,
	size,
	className,
	labelTop,
	background = "light",
	iconLight,
	iconDark,
	ctaLocation = "mobile_app_button",
}: Props) {
	const resolvedSize = size ?? (background === "light" ? "lg" : "sm");
	const iconSource = background === "dark" && iconDark ? iconDark : iconLight;
	const isApple = store.toLowerCase().includes("apple");
	const storeSlug = isApple ? "apple" : "google";
	const locationSlug = ctaLocation
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_|_$/g, "");
	const ctaId = `app_store_${storeSlug}_${locationSlug || "button"}`;

	const inner = (
		<>
			<div className="flex items-center gap-3 shrink-0">
				<Image
					src={iconSource}
					sizes="10vw"
					quality={100}
					alt=""
					aria-hidden
					className={cn(
						iconVariant({ size: resolvedSize }),
						"group-hover/mobile_app_button:brightness-0 group-hover/mobile_app_button:invert"
					)}
				/>
				<Separator
					orientation="vertical"
					className={cn("bg-white/20 transition-colors", separatorVariant({ size: resolvedSize }))}
				/>
			</div>
			<div className="flex flex-col leading-tight min-w-0 flex-1">
				<span className={spanVariants({ size: resolvedSize })}>{labelTop || "Get it on"}</span>
				<span
					className={cn(
						"font-semibold text-white group-hover/mobile_app_button:text-dark whitespace-nowrap",
						resolvedSize === "lg"
							? "text-[0.9rem] md:text-[clamp(0.65rem,1.2vw,0.9rem)]"
							: resolvedSize === "md"
								? "text-[0.8rem] md:text-[clamp(0.6rem,1.2vw,0.8rem)]"
								: resolvedSize === "sm"
									? "text-[0.7rem] md:text-[clamp(0.55rem,1.2vw,0.7rem)]"
									: "text-[0.55rem] md:text-[clamp(0.5rem,1.2vw,0.55rem)]",
					)}
				>
					{store}
				</span>
			</div>
		</>
	);

	return (
		<Button
			variant="ghost"
			asChild
			aria-label={`Go to ${store}`}
			className={cn(BACKGROUND_CLASS, containerVariant({ size: resolvedSize }), className)}
		>
			<Link
				target="_blank"
				href={href}
				className="min-w-0"
				{...getCtaDataAttrs({
					ctaId,
					ctaName: store,
					ctaLocation,
					linkUrl: href,
				})}
			>
				{inner}
			</Link>
		</Button>
	);
}

